import React from 'react'
import { Typography, Box, Container, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'

// Assets
import artImage from '../assets/art.jpg'
import nannyImage from '../assets/lansia.png'
import babyImage from '../assets/baby.jpg'

const SERVICE_PALETTE = [
  { accent: '#2563eb', lightBg: 'rgba(37,99,235,0.08)', borderColor: 'rgba(37,99,235,0.18)' },
  { accent: '#f97316', lightBg: 'rgba(249,115,22,0.08)', borderColor: 'rgba(249,115,22,0.18)' },
  { accent: '#059669', lightBg: 'rgba(5,150,105,0.08)',  borderColor: 'rgba(5,150,105,0.18)' },
]

const services = [
  {
    titleKey: 'services.babySitterTitle',
    descriptionKey: 'services.babySitterDescription',
    image: babyImage,
    num: '01',
    reverse: false,
  },
  {
    titleKey: 'services.houseAssistantTitle',
    descriptionKey: 'services.houseAssistantDescription',
    image: artImage,
    num: '02',
    reverse: true,
  },
  {
    titleKey: 'services.elderCareTitle',
    descriptionKey: 'services.elderCareDescription',
    image: nannyImage,
    num: '03',
    reverse: false,
  },
]

const Services = () => {
  const { t } = useTranslation()

  const handleWhatsAppClick = (serviceTitle) => {
    const message = `Halo CV Mulia Utama Mandiri, saya ingin menanyakan informasi lebih detail terkait layanan ${serviceTitle}. Saya mendapatkan informasi ini dari website Anda. Terima kasih.`
    window.open(`https://wa.me/${t('footer.number')}?text=${encodeURIComponent(message)}`, '_blank')
  }

  return (
    <Box sx={{ bgcolor: '#f8fafc', overflow: 'hidden' }}>

      {/* ── HERO ── */}
      <Box sx={{
        position: 'relative',
        minHeight: { xs: 'auto', md: 'auto' },
        display: 'flex',
        alignItems: 'flex-end',
        overflow: 'hidden',
        background: [
          'radial-gradient(ellipse 65% 70% at 12% 55%, rgba(37,99,235,0.24) 0%, transparent 65%)',
          'radial-gradient(ellipse 55% 60% at 88% 30%, rgba(249,115,22,0.14) 0%, transparent 60%)',
          '#060d1f',
        ].join(', '),
      }}>
        {/* Dot grid */}
        <Box sx={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.07) 1.5px, transparent 1.5px)',
          backgroundSize: '28px 28px',
          maskImage: 'radial-gradient(ellipse 85% 80% at 40% 50%, black 30%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 85% 80% at 40% 50%, black 30%, transparent 100%)',
        }} />
        {/* Concentric circle decoration — right */}
        {[480, 320, 160].map((size, i) => (
          <Box key={size} sx={{
            position: 'absolute',
            right: { xs: -160 + i * 40, md: -80 + i * 40 },
            top: '50%', transform: 'translateY(-50%)',
            width: size, height: size, borderRadius: '50%',
            border: `1px solid rgba(255,255,255,${0.04 + i * 0.025})`,
            ...(i === 2 && { bgcolor: 'rgba(249,115,22,0.05)' }),
            pointerEvents: 'none',
          }} />
        ))}
        {/* Small accent squares */}
        <Box sx={{ position: 'absolute', top: '22%', right: { xs: '10%', md: '21%' }, width: 10, height: 10, bgcolor: '#f97316', opacity: 0.5, borderRadius: '3px', transform: 'rotate(20deg)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: '30%', right: { xs: '18%', md: '28%' }, width: 6, height: 6, bgcolor: '#2563eb', opacity: 0.45, borderRadius: '2px', transform: 'rotate(35deg)', pointerEvents: 'none' }} />

        {/* Top accent bar */}
        <Box sx={{
          position: 'absolute',
          top: 0, left: 0, right: 0,
          height: 4,
          background: 'linear-gradient(90deg, #2563eb, #f97316)',
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: { xs: 9, md: 10 }, pb: { xs: 4, md: 5 } }}>
          {/* Orange overline */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <Box sx={{ width: 20, height: 2, bgcolor: '#f97316', borderRadius: 1 }} />
            <Typography sx={{
              color: '#f97316',
              fontWeight: 700,
              fontSize: '0.7rem',
              textTransform: 'uppercase',
              letterSpacing: 2,
            }}>
              CV. Mulia Utama Mandiri
            </Typography>
          </Stack>

          <Typography variant="h2" sx={{
            color: '#fff',
            fontWeight: 900,
            fontSize: { xs: '1.9rem', md: '2.8rem' },
            lineHeight: 1.1,
            letterSpacing: '-0.03em',
            mb: 1,
          }}>
            {t('services.title')}
          </Typography>

          <Typography sx={{
            color: 'rgba(255,255,255,0.75)',
            fontSize: '0.88rem',
            maxWidth: 480,
            lineHeight: 1.65,
          }}>
            {t('services.subTitle')}
          </Typography>
        </Container>
      </Box>

      {/* ── SERVICES SECTION ── */}
      <Box sx={{ bgcolor: '#ffffff', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          {services.map((service, index) => {
            const palette = SERVICE_PALETTE[index]
            const serviceName = t(service.titleKey)
            const isReverse = index === 1

            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: isReverse ? 'row-reverse' : 'row' },
                  alignItems: 'center',
                  gap: { xs: 5, md: 8 },
                  mb: { xs: 10, md: 16 },
                  '&:last-child': { mb: 0 },
                }}
              >
                {/* ── IMAGE SIDE ── */}
                <Box sx={{ flex: '0 0 48%', position: 'relative', width: '100%' }}>
                  {/* Decorative background box */}
                  <Box sx={{
                    position: 'absolute',
                    width: '65%',
                    height: '65%',
                    bgcolor: palette.lightBg,
                    border: `1.5px solid ${palette.borderColor}`,
                    borderRadius: '20px',
                    zIndex: 0,
                    ...(isReverse
                      ? { bottom: -20, right: -20 }
                      : { top: -20, left: -20 }),
                  }} />

                  {/* Ghost number watermark — desktop only */}
                  <Typography sx={{
                    display: { xs: 'none', md: 'block' },
                    fontSize: '10rem',
                    fontWeight: 950,
                    color: 'rgba(0,0,0,0.04)',
                    position: 'absolute',
                    bottom: -40,
                    [isReverse ? 'left' : 'right']: -30,
                    zIndex: 0,
                    lineHeight: 1,
                    userSelect: 'none',
                    pointerEvents: 'none',
                  }}>
                    {service.num}
                  </Typography>

                  {/* Photo container */}
                  <Box sx={{
                    position: 'relative',
                    zIndex: 1,
                    borderRadius: '20px',
                    overflow: 'hidden',
                    height: { xs: 280, md: 480 },
                    boxShadow: '0 24px 64px rgba(0,0,0,0.12)',
                  }}>
                    <Box
                      component="img"
                      src={service.image}
                      alt={serviceName}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center top',
                        display: 'block',
                      }}
                    />

                    {/* Frosted number badge — top-left of photo */}
                    <Box sx={{
                      position: 'absolute',
                      top: 16,
                      left: 16,
                      bgcolor: 'rgba(10,15,30,0.72)',
                      backdropFilter: 'blur(8px)',
                      color: '#fff',
                      fontWeight: 900,
                      fontSize: '0.78rem',
                      letterSpacing: '0.08em',
                      px: 1.5,
                      py: 0.6,
                      borderRadius: '20px',
                      border: '1px solid rgba(255,255,255,0.14)',
                      userSelect: 'none',
                    }}>
                      {service.num}
                    </Box>
                  </Box>
                </Box>

                {/* ── CONTENT SIDE ── */}
                <Box sx={{ flex: 1, position: 'relative' }}>
                  {/* Ghost number watermark behind content — desktop only */}
                  <Typography sx={{
                    display: { xs: 'none', md: 'block' },
                    fontSize: '10rem',
                    fontWeight: 950,
                    color: 'rgba(0,0,0,0.04)',
                    position: 'absolute',
                    top: -60,
                    [isReverse ? 'right' : 'left']: -30,
                    zIndex: 0,
                    lineHeight: 1,
                    userSelect: 'none',
                    pointerEvents: 'none',
                  }}>
                    {service.num}
                  </Typography>

                  <Box sx={{
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: { xs: 'center', md: 'flex-start' },
                    textAlign: { xs: 'center', md: 'left' },
                  }}>

                    {/* Title */}
                    <Typography variant="h3" sx={{
                      fontWeight: 900,
                      color: '#0f172a',
                      fontSize: { xs: '1.8rem', md: '2.8rem' },
                      lineHeight: 1.1,
                      letterSpacing: '-0.03em',
                      mb: 2,
                    }}>
                      {serviceName}
                    </Typography>

                    {/* Accent bar */}
                    <Box sx={{
                      width: 48,
                      height: 4,
                      bgcolor: palette.accent,
                      borderRadius: 2,
                      mb: 3,
                      alignSelf: { xs: 'center', md: 'flex-start' },
                    }} />

                    {/* Description */}
                    <Typography sx={{
                      color: '#475569',
                      lineHeight: 1.85,
                      fontSize: { xs: '0.95rem', md: '1.05rem' },
                      mb: 4,
                      textAlign: { xs: 'center', md: 'justify' },
                    }}>
                      {t(service.descriptionKey)}
                    </Typography>

                    {/* WhatsApp CTA */}
                    <Box
                      component="button"
                      onClick={() => handleWhatsAppClick(serviceName)}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1.2,
                        bgcolor: '#0f172a',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '50px',
                        px: 3.5,
                        py: 1.5,
                        fontSize: '0.88rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        letterSpacing: '0.01em',
                        transition: 'all 0.22s ease',
                        boxShadow: '0 4px 18px rgba(15,23,42,0.25)',
                        '&:hover': {
                          bgcolor: palette.accent,
                          boxShadow: `0 6px 22px ${palette.accent}55`,
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <FontAwesomeIcon icon={faWhatsapp} style={{ fontSize: '1rem' }} />
                      {t('services.exploreButton')}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )
          })}
        </Container>
      </Box>
    </Box>
  )
}

export default Services
